import { defineMessage } from 'react-intl';
import {
    AUTO_BOAT_AIR_EVENTBRITE_CATEGORY,
    BUSINESS_EVENTBRITE_CATEGORY,
    CHARITY_EVENTBRITE_CATEGORY,
    COMMUNITY_EVENTBRITE_CATEGORY,
    EVENTBRITE_CATEGORY_MAPPING,
    EVENTBRITE_FORMAT_MAPPING,
    FAMILY_EDUCATION_EVENTBRITE_CATEGORY,
    FASHION_EVENTBRITE_CATEGORY,
    FILM_EVENTBRITE_CATEGORY,
    FOOD_AND_DRINK_EVENTBRITE_CATEGORY,
    FORMATTED_SUBCATEGORY_MAP,
    HEALTH_EVENTBRITE_CATEGORY,
    HOBBIES_EVENTBRITE_CATEGORY,
    HOME_LIFESTYLE_EVENTBRITE_CATEGORY,
    MUSIC_EVENTBRITE_CATEGORY,
    OTHER_EVENTBRITE_CATEGORY,
    PERFORMING_EVENTBRITE_CATEGORY,
    POLITICS_EVENTBRITE_CATEGORY,
    RELIGION_EVENTBRITE_CATEGORY,
    SCHOOL_EVENTBRITE_CATEGORY,
    SCIENCE_EVENTBRITE_CATEGORY,
    SEASONAL_EVENTBRITE_CATEGORY,
    SPORTS_EVENTBRITE_CATEGORY,
    TRAVEL_EVENTBRITE_CATEGORY,
} from '@eventbrite/consumer-search.categories/constants';

export const EVENT_TYPE = 'event';
export const EVENTS_TYPE = 'events';
export const ALL_EVENTS_TYPE = 'all events';

/****************************** CATEGORY MAPPING ********************************/

export const CATEGORY_TAG_TYPE = 'Category';
export const SUBCATEGORY_TAG_TYPE = 'SubCategory';

// Translators: these are going to be used as part of a sentence
export const EVENTBRITE_CATEGORY_LOWERCASE_MAPPING = {
    [BUSINESS_EVENTBRITE_CATEGORY]: defineMessage({ id: 'business', defaultMessage: 'business' }),
    [SCIENCE_EVENTBRITE_CATEGORY]: defineMessage({
        id: 'science & tech',
        defaultMessage: 'science & tech',
    }),
    [MUSIC_EVENTBRITE_CATEGORY]: defineMessage({ id: 'music', defaultMessage: 'music' }),
    [FILM_EVENTBRITE_CATEGORY]: defineMessage({
        id: 'film & media',
        defaultMessage: 'film & media',
    }),
    [PERFORMING_EVENTBRITE_CATEGORY]: defineMessage({ id: 'arts', defaultMessage: 'arts' }),
    [FASHION_EVENTBRITE_CATEGORY]: defineMessage({ id: 'fashion', defaultMessage: 'fashion' }),
    [HEALTH_EVENTBRITE_CATEGORY]: defineMessage({ id: 'health', defaultMessage: 'health' }),
    [SPORTS_EVENTBRITE_CATEGORY]: defineMessage({
        id: 'sports & fitness',
        defaultMessage: 'sports & fitness',
    }),
    [TRAVEL_EVENTBRITE_CATEGORY]: defineMessage({
        id: 'travel & outdoor',
        defaultMessage: 'travel & outdoor',
    }),
    [FOOD_AND_DRINK_EVENTBRITE_CATEGORY]: defineMessage({
        id: 'food & drink',
        defaultMessage: 'food & drink',
    }),
    [CHARITY_EVENTBRITE_CATEGORY]: defineMessage({
        id: 'charity & causes',
        defaultMessage: 'charity & causes',
    }),
    [POLITICS_EVENTBRITE_CATEGORY]: defineMessage({
        id: 'government',
        defaultMessage: 'government',
    }),
    [COMMUNITY_EVENTBRITE_CATEGORY]: defineMessage({
        id: 'community',
        defaultMessage: 'community',
    }),
    [RELIGION_EVENTBRITE_CATEGORY]: defineMessage({
        id: 'spirituality',
        defaultMessage: 'spirituality',
    }),
    [FAMILY_EDUCATION_EVENTBRITE_CATEGORY]: defineMessage({
        id: 'family & education',
        defaultMessage: 'family & education',
    }),
    [SEASONAL_EVENTBRITE_CATEGORY]: defineMessage({ id: 'holiday', defaultMessage: 'holiday' }),
    [HOME_LIFESTYLE_EVENTBRITE_CATEGORY]: defineMessage({
        id: 'home & lifestyle',
        defaultMessage: 'home & lifestyle',
    }),
    [AUTO_BOAT_AIR_EVENTBRITE_CATEGORY]: defineMessage({
        id: 'auto, boat & air',
        defaultMessage: 'auto, boat & air',
    }),
    [HOBBIES_EVENTBRITE_CATEGORY]: defineMessage({ id: 'hobbies', defaultMessage: 'hobbies' }),
    [SCHOOL_EVENTBRITE_CATEGORY]: defineMessage({
        id: 'school activities',
        defaultMessage: 'school activities',
    }),
    [OTHER_EVENTBRITE_CATEGORY]: defineMessage({
        id: 'other',
        defaultMessage: 'other',
        description:
            "Other eventbrite category. This is used for events that don't fit into any other category. Example of categories: Food & Drink, Hobbies...",
    }),
};

/****************************** DATES MAPPING ****************************/

export const TODAY = 'today';
export const TOMORROW = 'tomorrow';
export const THIS_MONTH = 'this_month';
export const THIS_WEEK = 'this_week';
export const THIS_WEEKEND = 'this_weekend';
export const NEXT_WEEK = 'next_week';
export const NEXT_MONTH = 'next_month';

export const DATE_TAG = 'date';

// only used as default value, DON'T use it on the dropdowns (don't add it to DATES_MAPPING)
export const CURRENT_FUTURE = 'current_future';

export const CUSTOM_DATE = 'custom_date';

export const DATES_MAPPING = {
    [TODAY]: defineMessage({ id: 'Today', defaultMessage: 'Today' }),
    [TOMORROW]: defineMessage({ id: 'Tomorrow', defaultMessage: 'Tomorrow' }),
    [THIS_WEEKEND]: defineMessage({ id: 'This weekend', defaultMessage: 'This weekend' }),
    [THIS_WEEK]: defineMessage({ id: 'This week', defaultMessage: 'This week' }),
    [NEXT_WEEK]: defineMessage({ id: 'Next week', defaultMessage: 'Next week' }),
    [THIS_MONTH]: defineMessage({ id: 'This month', defaultMessage: 'This month' }),
    [NEXT_MONTH]: defineMessage({ id: 'Next month', defaultMessage: 'Next month' }),
    [CUSTOM_DATE]: defineMessage({ id: 'Pick a date...', defaultMessage: 'Pick a date...' }),
};

export const DATES_UPPERCASE_MAPPING = {
    [TODAY]: defineMessage({ id: 'Today', defaultMessage: 'Today' }),
    [TOMORROW]: defineMessage({ id: 'Tomorrow', defaultMessage: 'Tomorrow' }),
    [THIS_WEEKEND]: defineMessage({ id: 'This Weekend', defaultMessage: 'This Weekend' }),
    [THIS_WEEK]: defineMessage({ id: 'This Week', defaultMessage: 'This Week' }),
    [NEXT_WEEK]: defineMessage({ id: 'Next Week', defaultMessage: 'Next Week' }),
    [THIS_MONTH]: defineMessage({ id: 'This Month', defaultMessage: 'This Month' }),
    [NEXT_MONTH]: defineMessage({ id: 'Next Month', defaultMessage: 'Next Month' }),
    [CUSTOM_DATE]: defineMessage({ id: 'Pick a date...', defaultMessage: 'Pick a date...' }),
};

// Translators: these are going to be used as part of a sentence
export const DATES_LOWERCASE_MAPPING = {
    [TODAY]: defineMessage({ id: 'today', defaultMessage: 'today' }),
    [TOMORROW]: defineMessage({ id: 'tomorrow', defaultMessage: 'tomorrow' }),
    [THIS_WEEKEND]: defineMessage({ id: 'this weekend', defaultMessage: 'this weekend' }),
    [THIS_WEEK]: defineMessage({ id: 'this week', defaultMessage: 'this week' }),
    [NEXT_WEEK]: defineMessage({ id: 'next week', defaultMessage: 'next week' }),
    [THIS_MONTH]: defineMessage({ id: 'this month', defaultMessage: 'this month' }),
    [NEXT_MONTH]: defineMessage({ id: 'next month', defaultMessage: 'next month' }),
};

export const DATES_TO_REFINEMENT = {
    [TODAY]: 'today',
    [TOMORROW]: 'tomorrow',
    [THIS_WEEKEND]: 'this-weekend',
    [THIS_WEEK]: 'this-week',
    [NEXT_WEEK]: 'next-week',
    [THIS_MONTH]: 'this-month',
    [NEXT_MONTH]: 'next-month',
};

/****************************** EXPERIENCES MAPPING ****************************/
export const NIGHTLIFE_EXPERIENCE = 'nightlife'; // another NIGHTLIFE variable is defined lower in the file
export const KID_FRIENDLY_EXPERIENCE = 'kid-friendly';
export const GREAT_FOR_GROUPS_EXPERIENCE = 'great-for-groups';
export const DATE_NIGHT_EXPERIENCE = 'date-night';
export const BUDGET_FRIENDLY_EXPERIENCE = 'budget-friendly';

export const EXPERIENCES_MAPPING = {
    [NIGHTLIFE_EXPERIENCE]: defineMessage({ id: 'Nightlife', defaultMessage: 'Nightlife' }),
    [BUDGET_FRIENDLY_EXPERIENCE]: defineMessage({
        id: 'Budget-friendly',
        defaultMessage: 'Budget-friendly',
    }),
    [KID_FRIENDLY_EXPERIENCE]: defineMessage({
        id: 'Kid-friendly',
        defaultMessage: 'Kid-friendly',
    }),
    [GREAT_FOR_GROUPS_EXPERIENCE]: defineMessage({
        id: 'Great for groups',
        defaultMessage: 'Great for groups',
    }),
    [DATE_NIGHT_EXPERIENCE]: defineMessage({ id: 'Date night', defaultMessage: 'Date night' }),
};

export const EXPERIENCES_MAPPING_SEARCH = {
    [NIGHTLIFE_EXPERIENCE]: EXPERIENCES_MAPPING[NIGHTLIFE_EXPERIENCE],
    [KID_FRIENDLY_EXPERIENCE]: EXPERIENCES_MAPPING[KID_FRIENDLY_EXPERIENCE],
    [GREAT_FOR_GROUPS_EXPERIENCE]: EXPERIENCES_MAPPING[GREAT_FOR_GROUPS_EXPERIENCE],
    [DATE_NIGHT_EXPERIENCE]: EXPERIENCES_MAPPING[DATE_NIGHT_EXPERIENCE],
    [BUDGET_FRIENDLY_EXPERIENCE]: EXPERIENCES_MAPPING[BUDGET_FRIENDLY_EXPERIENCE],
};

/****************************** FORMAT MAPPING ****************************/

export const FORMAT_TAG_TYPE = 'Format';

/****************************** PRICE MAPPING ****************************/

const FREE = 'free';
const PAID = 'paid';

export const PRICE_MAPPING = {
    [FREE]: defineMessage({ id: 'Free', defaultMessage: 'Free' }),
    [PAID]: defineMessage({ id: 'Paid', defaultMessage: 'Paid' }),
};

/****************************** SORT MAPPING ****************************/

export const QUALITY = 'quality';

/****************************** SELECT FIELD CONSTANTS ********************************/
/*
 Receives a data map where the keys correlate to actual value, and the
 value correlates to what should be displayed. Reformats into a structure
 suitable for the <SelectField />

 @param dataMap - object
 {'this_week': 'This week', 'this_month': 'This month'}

 @return array
 [
    {value: 'this_week', display: 'This week'},
    {value: 'this_month', display: 'This month'}
]
*/
const _formatMapper = (
    dataMap: { [key: string]: string },
    valueKey: string,
    displayKey: string,
) => {
    return Object.keys(dataMap).reduce((memo: object[], key: string) => {
        const val = dataMap[key];

        return [...memo, { [valueKey]: key, [displayKey]: val }];
    }, []);
};

const _formatMappingForSelectField = (dataMap: any) => _formatMapper(dataMap, 'value', 'display');

export const FORMATTED_SUBCATEGORY_MAPPING = _formatMappingForSelectField(
    Object.fromEntries(
        Object.entries(FORMATTED_SUBCATEGORY_MAP).map(([key, subcategory]) => {
            return [key, subcategory.title];
        }),
    ),
);
export const FORMATTED_CATEGORY_MAPPING = _formatMappingForSelectField(EVENTBRITE_CATEGORY_MAPPING);
export const FORMATTED_FORMAT_MAPPING = _formatMappingForSelectField(EVENTBRITE_FORMAT_MAPPING);
export const FORMATTED_DATE_MAPPING = _formatMappingForSelectField(DATES_MAPPING);
export const FORMATTED_EXPERIENCES_MAPPING = _formatMappingForSelectField(EXPERIENCES_MAPPING);
export const FORMATTED_PRICE_MAPPING = _formatMappingForSelectField(PRICE_MAPPING);
